import { Button, Heading, VStack, Text, Box, Link } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useContext, useState } from 'react';
import { MortgageCertificateFormFields, MortgageCertificateRequest } from 'src/@types';
import {
  MortgageCertificateApplicantSection,
  MortgageCertificateFinalizeSection
} from './sections';
import { API } from '../../../../services';
import { getDefaultValues, prepareFormData } from './utils/mortgage-certificate-utils';
import { AppAlertDialog } from '../../../shared/general';
import { scrollTo } from '../../../../utils';
import { MortgageCertificateDetailsSection } from './sections/mortgage-certificate-details-section';
import { MortgageCertificateNewHouseSection } from './sections/mortgage-certificate-new-house-section';
import { FormContext } from '../../../../context/form-context';
import { IntlProvider } from 'react-intl';

export const MortgageCertificateForm = () => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid, isDirty }
  } = useForm<MortgageCertificateFormFields>({
    mode: 'onBlur',
    defaultValues: getDefaultValues()
  });

  const { isLoading, hasError, setLoading, setError } = useContext(FormContext);

  const values = watch();
  const [pdfUrl, setPdfUrl] = useState('#');
  const [activeStep, setActiveStep] = useState<1 | 2>(1);
  const [isEnterPressed, setIsEnterPressed] = useState(false);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.code === 'Enter') {
      setIsEnterPressed(true);
    }
  };

  async function onSubmit(values: MortgageCertificateFormFields) {
    if (isEnterPressed) {
      setIsEnterPressed(false);
      return;
    }
    setLoading(true);
    try {
      const res = await API.post<{ url: string }, MortgageCertificateRequest>(
        'certificate/hypothekercertificate',
        prepareFormData(values)
      );
      setPdfUrl(res.url);
      setActiveStep(2);
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
      scrollTo('.react-tools');
    }
  }

  const handleCloseAlert = () => {
    setError(false);
  };

  const Success = () => (
    <Box display={activeStep === 2 ? undefined : 'none'}>
      <Heading variant={'h1'} as={'h1'}>
        Hypotheker Certificaat
      </Heading>
      <Text>
        <Link href={pdfUrl} isExternal>
          Download PDF
        </Link>{' '}
        of <Link onClick={() => setActiveStep(1)}>vul deze opnieuw in</Link>.
      </Text>
    </Box>
  );

  return (
    <>
      <IntlProvider locale={`nl`} defaultLocale={'nl'}>
        <form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown}>
          <VStack display={activeStep === 1 ? undefined : 'none'} spacing={4} align="flex-start">
            <Heading variant={'h1'} as={'h1'}>
              Hypotheker Certificaat
            </Heading>

            <AppAlertDialog title={'let op!'} open={hasError} onClose={handleCloseAlert}>
              <Box>
                Door een technische storing kunnen we nu helaas geen hypotheker certificaat
                genereren. Probeer het opnieuw.
              </Box>
            </AppAlertDialog>

            <Box py={8}>
              <MortgageCertificateApplicantSection
                heading={'Gegevens aanvrager'}
                register={register}
                values={values}
                errors={errors}></MortgageCertificateApplicantSection>
            </Box>
            <Box py={8}>
              <MortgageCertificateDetailsSection
                heading={'Hypotheek gegevens'}
                register={register}
                values={values}
                errors={errors}></MortgageCertificateDetailsSection>
            </Box>
            <Box py={8}>
              <MortgageCertificateNewHouseSection
                heading={'Nieuwe woning (optioneel)'}
                register={register}
                values={values}
                errors={errors}></MortgageCertificateNewHouseSection>
            </Box>
            <Box py={8}>
              <MortgageCertificateFinalizeSection
                heading={'Ondertekenen'}
                register={register}
                values={values}
                setValue={setValue}
                errors={errors}></MortgageCertificateFinalizeSection>
            </Box>

            <Button
              isLoading={isLoading}
              loadingText={'Bezig met versturen'}
              type="submit"
              colorScheme="orange"
              isDisabled={false}>
              {!isValid && isDirty ? `Wat moet ik nog invullen?` : `Naar stap 2`}
            </Button>

            {/*<Text pt={4}>
              Werkt het Hypotheker Certificaat niet?{' '}
              <Link
                href={`https://jzkrplein.hypotheker.nl/umbraco/aanbod/hypotheker-certificaat`}
                isExternal>
                Hier
              </Link>{' '}
              download je een invulbare versie van het certificaat, zodat je 'm handmatig voor de
              klant in kunt vullen.
            </Text>*/}
          </VStack>
          <Success />
        </form>
      </IntlProvider>
    </>
  );
};
